// 一键成稿
import { defaultBatchSize, defaultHD, defaultPerturb, useReferenceColorWeight } from "../default";
import { ModuleEnum } from "../moduleEnum";
import type { ImageCommonState } from "@/types/image";
import type { Tag } from "@/types/prompt";
import { getModelEventTrackType, getStyleRefer, gethdFix } from "../index";

export const module = ModuleEnum["threeTransform"];

// 尺寸比例
const THREE_TRANSFORM_DEFAULT_ASPECT_RATIOS = "1:1";

// 生图参数
export const threeTransformParams = {
  module,
  advancedMode: false, // 高级模式（已弃用）
  imgTags: [] as Tag[], // 图片提示词标签
  prompt: "", // 用户输入的提示词
  imgTag: "", // 图像识别提示词
  negativePrompt: "", // 反向提示词
  negativePromptWeight: 0.4, // 反向提示词权重
  layoutReferId: null as null | number, // 原始稿件图片id
  layoutRefer: null as null | ImageCommonState, // 原始稿件
  layoutReferPose: null as null | ImageCommonState, // 任务姿势图片
  layoutReferMode: "draft" as "draft" | "pose", // 布局结构参考（draft: 草图，pose: 姿态）
  poseMode: "norm" as "norm" | "myself", // 当 layoutReferMode 为姿势时（norm: 官方，myself: 我的）
  maskImage: null as null | ImageCommonState, // 原始稿件蒙板图片
  maskContrastImage: null as null | ImageCommonState, // 原始稿件蒙板反相图片
  sketchType: 1, // 布局参考图类型：1-线稿，2-色块
  layoutReferWeight: useReferenceColorWeight, // 布局参考图权重
  colorRefer: false, // 参考颜色
  styleReferList: [] as any[], // 形象样式参考图片
  styleReferType: 1, // 形象样式参考风格类型（1: 默认 2: 构图 3: 风格 4: 脸部）
  styleReferWeight: 0.4, // 形象样式参考权重
  simpleBackground: true, // 简单画面背景
  faceDetail: false, // 面部精修
  aspectRatios: THREE_TRANSFORM_DEFAULT_ASPECT_RATIOS, // 尺寸比例
  batchSize: defaultBatchSize, // 生成数量
  hdFix: false, // 开启高清修复
  hdScale: defaultHD, // 高清修复数值
  enablePerturb: false, // 开启画面增强
  perturb: defaultPerturb, // 画面增强
  alchemy: false, // 语义增强引擎(Flux专用)
  imageGuidanceWeights: 2, // 画面指导权重
  threeDImg: "" // 3D模型图片
};

// 形象样式参考类型
export const styleTypeOptions = [
  {
    label: "默认",
    value: 1,
    multiple: true // 支持多图
  },
  {
    label: "构图",
    value: 2,
    multiple: true
  },
  {
    label: "风格",
    value: 3,
    multiple: true
  },
  {
    label: "脸部",
    value: 4,
    multiple: false // 不支持多图
  }
];

import { useArtworkStore } from "@/store/artwork";
import { useUserStore } from "@/store/user";
import { filteringInvalidFields } from "@/utils";
import { poseWeight } from "../default";
import type { ModuleRoute } from "@/types/artwork/generate";

// 将当前用户输入的生图参数转换成后端所需要
export async function getGenerateParam() {
  const artworkStore = useArtworkStore();
  const userStore = useUserStore();
  const modelViewerStore = useModelViewerStore();

  let params = {
    module: threeTransformParams.module,
    userId: userStore.userInfo.id,
    uniqueId: userStore.userInfo.uniqueId,
    artworkId: artworkStore.artworkState.id,
    modelList: artworkStore.artworkState.modelList,
    threeDImg: modelViewerStore.modelUrl,
    attach: JSON.stringify({
      originalImageUrl: modelViewerStore.imageUrl,
      removeBackgroundImageUrl: modelViewerStore.removeBackgroundImageUrl,
      backgroundColor: modelViewerStore.currentBackgroundColor
    }),
    loraList: artworkStore.artworkState.modelList.map((item) => {
      return {
        modelId: item.id,
        strength: item.strength
      };
    })
  } as any;

  const layoutRefer = artworkStore.threeTransform.params.layoutRefer;
  const layoutReferId = layoutRefer?.id;

  if (artworkStore.threeTransform.params.layoutReferMode === "draft") {
    params.layoutReferPose = null;
  } else {
    params.layoutRefer = null;
    params.layoutReferWeight = poseWeight;
  }

  params = {
    ...artworkStore.threeTransform.params,
    ...params,
    source: 1,
    aspectRatios: THREE_TRANSFORM_DEFAULT_ASPECT_RATIOS,
    styleReferIdList: artworkStore.threeTransform.params.styleReferList.map((item) => {
      return {
        styleReferId: item.id,
        weight: item.weight / 100
      };
    }),
    layoutReferId: layoutReferId || null
  };

  params.maskId = artworkStore.threeTransform.params.maskContrastImage?.id || null;
  if (artworkStore.threeTransform.params.layoutReferMode !== "draft" || artworkStore.threeTransform.params.sketchType !== 2) {
    delete params.maskImage;
    delete params.maskContrastImage;
    params.maskId = null;
  }

  const targetModule = ModuleEnum[threeTransformParams.module] as ModuleRoute;
  // 图片提示词
  params.imgTag = artworkStore.threeTransform.params.imgTags.map((item) => item.enName).join(",");

  // 去除 layoutRefer 中的无效字段
  filteringInvalidFields(params.layoutRefer, ["id", "width", "height", "image"]);

  /**
   * Flux模型
   * 非V1模型用的是色块草图+颜色参考,V1的话用基本色块草图就可以了
   */
  if (params.modelList[0]?.ckptType === "Flux") {
    params.negativePrompt = params.alchemy ? params.negativePrompt : "";

    delete params.maskImage;
    delete params.maskContrastImage;
    params.maskId = null;
  } else {
    params.colorRefer = true;
  }

  return params;
}

import { useEventTracking } from "@/hooks/useEventTracking";
import type { ModelState } from "@/types/model";
import { startModuleGenerate } from "./index";
import { useModelViewerStore } from "@/store/modelViewer";
import { useModelStore } from "@/store/model";
import { styleModelOptions } from "@/constants";
import { get } from "lodash";
import { getModuleLabelById } from "@xmiles/holopix-helper";

// 开启模块生图
export async function startThreeTransformGenerate(
  oldParams?: any,
  options?: {
    onClientIdGenerated?: (clientId: string) => void;
    onClientIdCleared?: () => void;
  }
) {
  const artworkStore = useArtworkStore();
  const modelStore = useModelStore();
  const params = await getGenerateParam();

  const module = threeTransformParams.module;
  const modelList = get(params, "modelList", []) || [];
  const modelLabels = modelList.flatMap((item:any) => item.label || []);
  const modelNameList = modelList.map((i: any) => i.modelName) || [];
  // 按照 modelLabels 中 ID 的顺序过滤和排序标签
  const modelTagList = modelLabels
    .map((labelId:any) => modelStore.labelOptions.find((item: any) => item.value === labelId))
    .filter(Boolean)
    .map((item:any) => item!.label);

  const modelCategories = modelList.map((item:any) => item.category).filter(Boolean);

  const modelTypeList = modelCategories
    .map((categoryId:any) => modelStore.categoryOptions.find((item: any) => item.value === categoryId))
    .filter(Boolean)
    .map((item:any) => item!.label);

  // 创建loraType映射关系
  const loraTypeMap = Object.fromEntries(styleModelOptions.map((option) => [option.value, option.label]));
  // 提取loraType对应的label组成数组
  const loraTypeLabels = modelList.map((item:any) => (item.loraType ? loraTypeMap[item.loraType] || "视频模型" : item.loraType));
  const isUserGeneratedText = (() => {
    if (modelList.every((i:any) => i.userId !== 0)) return "是";
    if (modelList.every((i:any) => i.userId === 0)) return "否";
    return "混合";
  })();
  const modelIdList = modelList.map((item: ModelState) => item.id);

  startModuleGenerate(module, {
    getModuleParams: () => {
      return oldParams || params;
    },
    onStartTrack: (clientId) => {
      reportStartEventTracking(clientId);
    },
    onFinishTrack: (data) => {
      reportFinishEventTracking(data);
    },
    onClientIdGenerated: options?.onClientIdGenerated,
    onClientIdCleared: options?.onClientIdCleared
  });

  // 提交生图埋点
  function reportStartEventTracking(clientId: string) {
    const eventValue: any = {
      client_id: clientId,
      module: getModuleLabelById(module) as string,
      source: params.source === 1 ? "直接生成" : "再迭代",
      artwork_id: artworkStore.artworkState.id.toString(),
      prompt: params.prompt,
      negative_prompt: params.negativePrompt,
      face_detail: params.faceDetail ? 1 : 0,
      style_refer: params.styleReferList?.length > 0 ? "1" : "0",
      batchSize: params.batchSize,
      // model_id: params.modelList.map((item: ModelState) => item.id).join(","),
      hp_model_id: String(modelIdList.join(",")),

      model_name: params.modelList.map((item: ModelState) => item.modelName).join(",") || JSON.stringify(params.modelList || ["--"]),
      ckpt_type: params.modelList.map((item: ModelState) => item.ckptType).join(","),
      lora_type: params.modelList.map((item: ModelState) => item.loraType).join(","),
      is_personal_model: getModelEventTrackType(params.modelList),
      model_tags: modelTagList.join(","),

      model_type: modelTypeList.join(","),
      hp_model_id_list: modelIdList,
      hp_model_name_list: modelNameList,
      hp_model_tags_list: modelTagList,
      hp_model_type_list: modelTypeList,
      hp_is_user_generated: isUserGeneratedText,
      is_composite_model: loraTypeLabels
    };

    // 形象样式参考埋点值
    if (getStyleRefer(params.styleReferType, params.styleReferList)) {
      eventValue.style_refer = getStyleRefer(params.styleReferType, params.styleReferList);
    }

    // 高清修复
    if (gethdFix(params.hdFix, params.hdScale)) {
      eventValue.hd_fix = gethdFix(params.hdFix, params.hdScale);
    }

    // 语义增强
    eventValue.semantics_enhance = params.alchemy ? 1 : 0;

    useEventTracking({
      event: "creation_event",
      eventValue
    });
  }

  function reportFinishEventTracking({
    client_id = "",
    result = 0 as 0 | 1,
    imageIds = "",
    queue_time_number = 0,
    loading_time_number = 0,
    vipChannel = false
  } = {}) {
    const eventValue: any = {
      module: getModuleLabelById(module) as string,
      client_id,
      source: params.source === 1 ? "直接生成" : "再迭代",
      img_id: imageIds,
      result,
      queue_time_number,
      loading_time_number,
      // model_id: params.modelList.map((item: ModelState) => item.id).join(","),
      hp_model_id: String(modelIdList.join(",")),
      hp_model_id_list: modelIdList,
      model_name: params.modelList.map((item: ModelState) => item.modelName).join(",") || JSON.stringify(params.modelList || ["--"]),
      ckpt_type: params.modelList.map((item: ModelState) => item.ckptType).join(","),
      first_model_ckpt: params.modelList[0]?.ckptType,
      vipChannel: vipChannel,
      lora_type: params.modelList.map((item: ModelState) => item.loraType).join(","),
      is_personal_model: getModelEventTrackType(params.modelList),

      prompt: params.prompt,
      negative_prompt: params.negativePrompt,
      face_detail: params.faceDetail ? 1 : 0,
      style_refer: params.styleReferList?.length > 0 ? "1" : "0",
      model_tags: modelTagList.join(","),
      hp_model_tags_list: modelTagList,
      model_type: modelTypeList.join(","),
      hp_model_type_list: modelTypeList,
      hp_is_user_generated: isUserGeneratedText
    };

    // 形象样式参考埋点值
    if (getStyleRefer(params.styleReferType, params.styleReferList)) {
      eventValue.style_refer = getStyleRefer(params.styleReferType, params.styleReferList);
    }

    // 高清修复
    if (gethdFix(params.hdFix, params.hdScale)) {
      eventValue.hd_fix = gethdFix(params.hdFix, params.hdScale);
    }

    // 语义增强
    eventValue.semantics_enhance = params.alchemy ? 1 : 0;

    useEventTracking({
      event: "artwork_status_event",
      eventValue
    });
  }
}
